import { useState } from "react";
import { Plus, Trash2, Building2, Pencil, MapPin, Users, X, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AddressAutocomplete } from "../AddressAutocomplete";
import { useMasters } from "./MastersContext";

const KIND_LABEL = { pickup: "Pickup", delivery: "Delivery", both: "Pickup & Delivery" };

// Quote-aware CSV line splitter so cells like "Plot 1, Sector 5, Pune" stay intact.
function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (q) {
      if (ch === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else q = false;
      } else cur += ch;
    } else if (ch === '"') q = true;
    else if (ch === ",") { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.trim());
}

export function DeptContactsSection() {
  const navigate = useNavigate();
  const {
    customers, selCompany, setSelCompany, departments, contacts, companyAddresses,
    deptForm, setDeptForm, addDept, delDept,
    contactForm, setContactForm, editingContactId, addContact, editContact, cancelEditContact, delContact,
    addressForm, setAddressForm, addAddress, delAddress, bulkImportEmployees, importEmployeesFile, importPreviewFile, importMapped,
  } = useMasters();
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [previewRows, setPreviewRows] = useState(null);   // array of arrays once parsed
  const [skipFirst, setSkipFirst] = useState(true);
  const FIELDS = [["name", "Name*"], ["department", "Department"], ["phone", "Phone"], ["email", "Email"], ["designation", "Designation"]];
  const [mapping, setMapping] = useState({ name: 0, department: 1, phone: 2, email: 3, designation: 4 });

  function guessMapping(rows) {
    const header = (rows[0] || []).map((c) => String(c).toLowerCase().trim());
    const looksHeader = header.some((h) => ["name", "department", "dept", "phone", "email", "designation", "mobile"].includes(h));
    const m = { name: -1, department: -1, phone: -1, email: -1, designation: -1 };
    const pats = { name: ["name", "employee"], department: ["department", "dept"], phone: ["phone", "mobile", "contact"], email: ["email", "mail"], designation: ["designation", "role", "title"] };
    if (looksHeader) {
      for (const [field, keys] of Object.entries(pats)) {
        const idx = header.findIndex((h) => keys.some((k) => h.includes(k)));
        if (idx >= 0) m[field] = idx;
      }
    } else {
      const n = (rows[0] || []).length;
      ["name", "department", "phone", "email", "designation"].forEach((f, i) => { m[f] = i < n ? i : -1; });
    }
    setMapping(m); setSkipFirst(looksHeader);
  }

  function parsePaste() {
    const rows = importText.split(/\r?\n/).filter((l) => l.trim()).map((l) => (l.includes("\t") ? l.split("\t").map((c) => c.trim()) : splitCsvLine(l)));
    if (!rows.length) { toast.error("Paste at least one line"); return; }
    setPreviewRows(rows); guessMapping(rows);
  }
  async function parseFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try { const rows = await importPreviewFile(file); if (!rows.length) { toast.error("No rows in file"); return; } setPreviewRows(rows); guessMapping(rows); }
    catch { toast.error("Could not read file"); }
    finally { setImporting(false); }
  }
  async function doImportMapped() {
    if (mapping.name < 0) { toast.error("Map the Name column"); return; }
    setImporting(true);
    try {
      const r = await importMapped(previewRows, mapping, skipFirst);
      toast.success(`Imported ${r.imported} employee${r.imported === 1 ? "" : "s"}${r.departments_created ? ` · ${r.departments_created} new dept(s)` : ""}`);
      setImportText(""); setPreviewRows(null); setImportOpen(false);
    } catch { toast.error("Import failed"); }
    finally { setImporting(false); }
  }
  function resetImport() { setPreviewRows(null); setImportText(""); }

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "";

  return (
    <section className="lg:col-span-2">
      <div className="flex items-center gap-2 mb-3"><Building2 className="w-4 h-4 text-[#C84B31]" /><div className="section-title mb-0">Companies · Departments · Employees · Addresses</div></div>
      <div className="card-surface p-4 mb-4">
        <label className="input-label">Pick a company to manage its structure</label>
        <select className="input-field md:w-96" value={selCompany} onChange={(e) => setSelCompany(e.target.value)} data-testid="dept-company-select">
          <option value="">— Select company —</option>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        {selCompany && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-5">
            {/* Departments */}
            <div>
              <div className="section-title flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Departments</div>
              <form onSubmit={addDept} className="flex gap-2 mb-3">
                <input className="input-field flex-1" placeholder="Department name" value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} required data-testid="dept-name-input" />
                <button type="submit" className="btn-primary" data-testid="add-dept-button"><Plus className="w-4 h-4" /></button>
              </form>
              <div className="border border-[#E5E3DB] divide-y divide-[#F0EDE5]">
                {departments.length === 0 ? <div className="px-3 py-2 text-sm text-[#A09E96]">No departments yet.</div> :
                  departments.map((d) => (
                    <div key={d.id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`dept-row-${d.id}`}>
                      <button className="text-[#C84B31] hover:underline text-left" onClick={() => navigate(`/entity/department/${d.id}`)} data-testid={`dept-open-${d.id}`}>{d.name}</button>
                      <button className="text-[#8A2526]" onClick={() => delDept(d.id)} data-testid={`dept-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
              </div>
            </div>

            {/* Employees */}
            <div>
              <div className="section-title flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Employees</div>
              <form onSubmit={addContact} className="space-y-2 mb-3">
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-field" placeholder="Name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} required data-testid="contact-name-input" />
                  <input className="input-field" placeholder="Designation" value={contactForm.designation} onChange={(e) => setContactForm({ ...contactForm, designation: e.target.value })} data-testid="contact-designation-input" />
                  <input className="input-field" placeholder="Phone" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} data-testid="contact-phone-input" />
                  <input className="input-field" placeholder="Email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} data-testid="contact-email-input" />
                </div>
                <select className="input-field" value={contactForm.department_id} onChange={(e) => setContactForm({ ...contactForm, department_id: e.target.value })} data-testid="contact-dept-select">
                  <option value="">Company-wide (no department)</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
                <AddressAutocomplete className="input-field w-full" placeholder="Default address (auto-fills on pick)" value={contactForm.address} onChange={(v) => setContactForm({ ...contactForm, address: v })} onSelect={(v) => setContactForm({ ...contactForm, address: v })} testId="contact-address-input" />
                <input className="input-field" placeholder="GSTIN (optional)" value={contactForm.gst} onChange={(e) => setContactForm({ ...contactForm, gst: e.target.value })} data-testid="contact-gst-input" />
                <div className="flex gap-2">
                  <button type="submit" className="btn-primary flex-1" data-testid="add-contact-button"><Plus className="w-4 h-4" /> {editingContactId ? "Save employee" : "Add Employee"}</button>
                  {editingContactId && <button type="button" className="btn-ghost" onClick={cancelEditContact} data-testid="contact-cancel-edit"><X className="w-4 h-4" /></button>}
                </div>
              </form>
              <div className="mb-3">
                <button type="button" className="btn-ghost text-xs" onClick={() => setImportOpen((v) => !v)} data-testid="import-employees-toggle"><Upload className="w-3.5 h-3.5" /> Import list</button>
                {importOpen && (
                  <div className="mt-2 p-2 border border-[#DCE7F6] bg-[#F2F6FB] rounded">
                    {!previewRows ? (
                      <>
                        <p className="text-[11px] text-[#68665E] mb-1.5">Paste rows (TAB or comma separated) or upload a file — you'll map the columns next.</p>
                        <textarea className="input-field w-full font-mono text-xs" rows={4} placeholder={"Ramesh K, Dispatch, 9800011122, ramesh@co.com, Manager\nSita R, Accounts, 9800022233"} value={importText} onChange={(e) => setImportText(e.target.value)} data-testid="import-employees-textarea" />
                        <button type="button" className="btn-primary w-full mt-2" onClick={parsePaste} disabled={importing} data-testid="import-employees-parse"><Upload className="w-4 h-4" /> Preview & map</button>
                        <div className="text-center text-[11px] text-[#A09E96] my-1.5">— or upload a file —</div>
                        <label className="btn-secondary w-full cursor-pointer justify-center" data-testid="import-employees-file-label">
                          <Upload className="w-4 h-4" /> Upload .xlsx / .csv
                          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={parseFile} disabled={importing} data-testid="import-employees-file" />
                        </label>
                      </>
                    ) : (
                      <div data-testid="import-mapping-panel">
                        <p className="text-[11px] text-[#68665E] mb-2">Map each field to a column ({previewRows.length} row{previewRows.length === 1 ? "" : "s"} detected). Sample from row 1 shown.</p>
                        <div className="space-y-1.5 mb-2">
                          {FIELDS.map(([field, label]) => (
                            <div key={field} className="flex items-center gap-2">
                              <span className="text-xs text-[#68665E] w-24 shrink-0">{label}</span>
                              <select className="input-field !py-1 text-xs flex-1" value={mapping[field]} onChange={(e) => setMapping((m) => ({ ...m, [field]: parseInt(e.target.value, 10) }))} data-testid={`import-map-${field}`}>
                                <option value={-1}>— none —</option>
                                {(previewRows[0] || []).map((cell, ci) => <option key={ci} value={ci}>Col {ci + 1} · {String(cell).slice(0, 20) || "(empty)"}</option>)}
                              </select>
                            </div>
                          ))}
                        </div>
                        <label className="flex items-center gap-2 text-xs text-[#68665E] mb-2"><input type="checkbox" checked={skipFirst} onChange={(e) => setSkipFirst(e.target.checked)} data-testid="import-skip-first" /> First row is a header (skip it)</label>
                        <div className="flex gap-2">
                          <button type="button" className="btn-primary flex-1" onClick={doImportMapped} disabled={importing} data-testid="import-mapped-submit"><Upload className="w-4 h-4" /> {importing ? "Importing…" : "Import"}</button>
                          <button type="button" className="btn-ghost" onClick={resetImport} data-testid="import-mapping-back"><X className="w-4 h-4" /></button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="border border-[#E5E3DB] divide-y divide-[#F0EDE5]">
                {contacts.length === 0 ? <div className="px-3 py-2 text-sm text-[#A09E96]">No employees yet.</div> :
                  contacts.map((ct) => (
                    <div key={ct.id} className="flex items-start justify-between px-3 py-2 text-sm gap-2" data-testid={`contact-row-${ct.id}`}>
                      <button className="text-left hover:underline text-[#C84B31] min-w-0" onClick={() => navigate(`/entity/contact/${ct.id}`)} data-testid={`contact-open-${ct.id}`}>
                        <span className="font-medium">{ct.name}</span>{ct.designation ? ` · ${ct.designation}` : ""}
                        <span className="block text-[11px] text-[#68665E] truncate">{[ct.phone, ct.department_id ? deptName(ct.department_id) : "Company-wide", ct.address].filter(Boolean).join(" · ")}</span>
                      </button>
                      <div className="flex items-center gap-1 shrink-0">
                        <button className="text-[#68665E]" onClick={() => editContact(ct)} data-testid={`contact-edit-${ct.id}`}><Pencil className="w-4 h-4" /></button>
                        <button className="text-[#8A2526]" onClick={() => delContact(ct.id)} data-testid={`contact-delete-${ct.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Pickup / Delivery Addresses */}
            <div>
              <div className="section-title flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Pickup / Delivery Addresses</div>
              <form onSubmit={addAddress} className="space-y-2 mb-3">
                <div className="grid grid-cols-2 gap-2">
                  <select className="input-field" value={addressForm.kind} onChange={(e) => setAddressForm({ ...addressForm, kind: e.target.value })} data-testid="address-kind-select">
                    <option value="pickup">Pickup</option>
                    <option value="delivery">Delivery</option>
                    <option value="both">Both</option>
                  </select>
                  <input className="input-field" placeholder="Label (e.g. Taloja Plant)" value={addressForm.label} onChange={(e) => setAddressForm({ ...addressForm, label: e.target.value })} data-testid="address-label-input" />
                </div>
                <AddressAutocomplete className="input-field w-full" placeholder="Address" value={addressForm.address} onChange={(v) => setAddressForm({ ...addressForm, address: v })} onSelect={(v) => setAddressForm({ ...addressForm, address: v })} testId="address-address-input" />
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-field" placeholder="GSTIN (optional)" value={addressForm.gst} onChange={(e) => setAddressForm({ ...addressForm, gst: e.target.value })} data-testid="address-gst-input" />
                  <select className="input-field" value={addressForm.department_id} onChange={(e) => setAddressForm({ ...addressForm, department_id: e.target.value })} data-testid="address-dept-select">
                    <option value="">Any department</option>
                    {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <button type="submit" className="btn-primary w-full" data-testid="add-address-button"><Plus className="w-4 h-4" /> Add Address</button>
              </form>
              <div className="border border-[#E5E3DB] divide-y divide-[#F0EDE5]">
                {companyAddresses.length === 0 ? <div className="px-3 py-2 text-sm text-[#A09E96]">No saved addresses yet.</div> :
                  companyAddresses.map((a) => (
                    <div key={a.id} className="flex items-start justify-between px-3 py-2 text-sm gap-2" data-testid={`address-row-${a.id}`}>
                      <div className="min-w-0">
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-[#EAF0FB] text-[#2B5FB3]">{KIND_LABEL[a.kind] || a.kind}</span>
                        {a.label && <span className="font-medium ml-1.5">{a.label}</span>}
                        <span className="block text-[11px] text-[#68665E] truncate">{[a.address, a.department_id ? deptName(a.department_id) : "", a.gst].filter(Boolean).join(" · ")}</span>
                      </div>
                      <button className="text-[#8A2526] shrink-0" onClick={() => delAddress(a.id)} data-testid={`address-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
